<template>
  <div class="card">
    <div class="card-header bg-info text-white">
      <h5 class="mb-0">LED设置</h5>
    </div>
    <div class="card-body">
      <!-- LED 显示设置 -->
      <div class="mb-4">
        <h5 class="fw-bold mb-3">文字颜色设置</h5>
        <div class="row g-3 align-items-center mb-3">
          <div class="col-md-6 d-flex align-items-center">
            <label class="form-label me-2">站名文字颜色：</label>
            <select class="form-select w-auto" v-model="ledSettings.stationTextColor">
              <option value="255">红色</option>
              <option value="65280">绿色</option>
              <option value="16711680">蓝色</option>
              <option value="65535">黄色</option>
              <option value="42495">橙色</option>
              <option value="16777215">白色</option>
            </select>
          </div>
          <div class="col-md-6 d-flex align-items-center">
            <label class="form-label me-2">服务信息文字颜色：</label>
            <select class="form-select w-auto" v-model="ledSettings.serviceTextColor">
              <option value="255">红色</option>
              <option value="65280">绿色</option>
              <option value="16711680">蓝色</option>
              <option value="65535">黄色</option>
              <option value="42495">橙色</option>
              <option value="16777215">白色</option>
            </select>
          </div>
        </div>
      </div>

      <!-- 滚动设置 -->
      <div class="mb-4">
        <h5 class="fw-bold mb-3">滚动方式设置</h5>
        <div class="row g-3 align-items-center mb-3">
          <div class="col-md-6 d-flex align-items-center">
            <label class="form-label me-2">站名滚动方式：</label>
            <select class="form-select w-auto" v-model="ledSettings.stationNoScrollMode" @change="validateScrollSettings('station')">
              <option value="still">静止显示</option>
              <option value="left">连续向左滚动</option>
              <option value="top">连续向上滚动</option>
            </select>
          </div>
          <div class="col-md-6 d-flex align-items-center">
            <label class="form-label me-2">服务信息滚动方式：</label>
            <select class="form-select w-auto" v-model="ledSettings.serviceNoScrollMode" @change="validateScrollSettings('service')">
              <option value="still">静止显示</option>
              <option value="left">连续向左滚动</option>
              <option value="top">连续向上滚动</option>
            </select>
          </div>
        </div>
      </div>

      <!-- 速度设置 -->
      <div class="mb-4">
        <h5 class="fw-bold mb-3">滚动速度设置</h5>
        <div class="row g-3 align-items-center mb-3">
          <div class="col-md-6 d-flex align-items-center">
            <label class="form-label me-2">向左滚动速度：</label>
            <input type="range" class="form-range flex-grow-1 me-2" min="1" max="20" v-model.number="ledSettings.leftScrollSpeed">
            <span class="form-text">{{ ledSettings.leftScrollSpeed }}</span>
          </div>
          <div class="col-md-6 d-flex align-items-center">
            <label class="form-label me-2">向上滚动速度：</label>
            <input type="range" class="form-range flex-grow-1 me-2" min="1" max="20" v-model.number="ledSettings.upScrollSpeed">
            <span class="form-text">{{ ledSettings.upScrollSpeed }}</span>
          </div>
        </div>
      </div>

      <!-- LED文本模板 -->
      <div class="mb-4">
        <h5 class="fw-bold mb-3">LED文本模板</h5>
        <!-- 欢迎语自定义文本框 -->
        <div class="mb-3">
          <label class="form-label">欢迎语LED显示内容</label>
          <div class="row g-2 align-items-center mb-2">
            <div class="col-md-6 d-flex align-items-center">
              <span class="text-muted me-2" style="min-width: 40px;">中文</span>
              <input type="text" class="form-control" 
                     v-model="ledSettings.welcomeLEDContent" 
                     placeholder="输入欢迎语LED显示内容（中文）"
                     @focus="setFocusedField('welcomeLEDContent', 'zh')">
            </div>
            <template v-if="languageSettings && languageSettings.enabled && visibleLanguages.length > 1">
              <div v-for="(lang, idx) in nonChineseLanguages" :key="'welcome-lang-' + (lang.code || idx)" class="col-md-6 d-flex align-items-center">
                <span class="text-muted me-2" style="min-width: 40px;">{{ lang.name }}</span>
                <input type="text" class="form-control" 
                       v-model="ledSettings['welcomeLEDContent_' + lang.code]" 
                       :placeholder="`输入欢迎语LED显示内容（${lang.name}）`"
                       @focus="setFocusedField('welcomeLEDContent', lang.code)">
              </div>
            </template>
          </div>
          
        </div>
        <div class="mb-3">
          <label class="form-label">进站LED显示内容</label>
          <div class="row g-2 align-items-center mb-2">
            <div class="col-md-6 d-flex align-items-center">
              <span class="text-muted me-2" style="min-width: 40px;">中文</span>
              <input type="text" class="form-control" 
                     v-model="ledSettings.arrivalLEDContent" 
                     placeholder="输入进站时LED显示的内容（中文）"
                     @focus="setFocusedField('arrivalLEDContent', 'zh')">
            </div>
            <template v-if="languageSettings && languageSettings.enabled && visibleLanguages.length > 1">
              <div v-for="(lang, idx) in nonChineseLanguages" :key="'arrival-lang-' + (lang.code || idx)" class="col-md-6 d-flex align-items-center">
                <span class="text-muted me-2" style="min-width: 40px;">{{ lang.name }}</span>
                <input type="text" class="form-control" 
                       v-model="ledSettings['arrivalLEDContent_' + lang.code]" 
                       :placeholder="`输入进站时LED显示的内容（${lang.name}）`"
                       @focus="setFocusedField('arrivalLEDContent', lang.code)">
              </div>
            </template>
          </div>
          <div class="form-text">使用【当前站】等占位符，会自动替换为实际站名</div>
        </div>
        
        <div class="mb-3">
          <label class="form-label">出站LED显示内容</label>
          <div class="row g-2 align-items-center mb-2">
            <div class="col-md-6 d-flex align-items-center">
              <span class="text-muted me-2" style="min-width: 40px;">中文</span>
              <input type="text" class="form-control" 
                     v-model="ledSettings.departureLEDContent" 
                     placeholder="输入出站时LED显示的内容（中文）"
                     @focus="setFocusedField('departureLEDContent', 'zh')">
            </div>
            <template v-if="languageSettings && languageSettings.enabled && visibleLanguages.length > 1">
              <div v-for="(lang, idx) in nonChineseLanguages" :key="'departure-lang-' + (lang.code || idx)" class="col-md-6 d-flex align-items-center">
                <span class="text-muted me-2" style="min-width: 40px;">{{ lang.name }}</span>
                <input type="text" class="form-control" 
                       v-model="ledSettings['departureLEDContent_' + lang.code]" 
                       :placeholder="`输入出站时LED显示的内容（${lang.name}）`"
                       @focus="setFocusedField('departureLEDContent', lang.code)">
              </div>
            </template>
          </div>
        </div>
        <!-- 占位符插入按钮区域 -->
        <div class="mt-2 mb-3">
          <label class="form-label">占位符插入：</label>
          <div class="d-flex flex-wrap gap-1 mb-2">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="addPlaceholder('【当前站】')">当前站</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="addPlaceholder('【起点站】')">起点站</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="addPlaceholder('【终点站】')">终点站</button>
          </div>
          <!-- 多语言占位符按钮 -->
          <div v-if="languageSettings && languageSettings.enabled && visibleLanguages.length > 1" class="mt-2">
            <label class="form-label">多语言占位符：</label>
            <div class="d-flex flex-wrap gap-1">
              <template v-for="(lang, idx) in nonChineseLanguages" :key="'welcome-multi-'+(lang.code || idx)">
                <button type="button" class="btn btn-sm btn-outline-info" @click="addPlaceholder(`【当前站(${lang.name})】`)">当前站({{lang.name}})</button>
                <button type="button" class="btn btn-sm btn-outline-info" @click="addPlaceholder(`【起点站(${lang.name})】`)">起点站({{lang.name}})</button>
                <button type="button" class="btn btn-sm btn-outline-info" @click="addPlaceholder(`【终点站(${lang.name})】`)">终点站({{lang.name}})</button>
              </template>
            </div>
          </div>
        </div>
        
        <!-- 服务语LED显示内容 -->
        <div class="mb-3">
          <label class="form-label">服务语LED显示内容</label>
          
          <!-- 服务语分组显示 -->
          <div class="accordion" id="serviceAccordion">
            <div class="accordion-item" v-for="i in 10" :key="'service' + i">
              <h2 class="accordion-header">
                <button class="accordion-button" type="button" 
                        :class="{ 'collapsed': !isServiceExpanded(i) }"
                        @click="toggleServiceExpanded(i)"
                        :aria-expanded="isServiceExpanded(i)" 
                        :aria-controls="'service' + i + 'Collapse'">
                  服务语{{ i }}
                </button>
              </h2>
              <div :id="'service' + i + 'Collapse'" class="accordion-collapse" :class="{ 'collapse': !isServiceExpanded(i), 'show': isServiceExpanded(i) }">
                <div class="accordion-body">
                  <div class="row g-2 align-items-center mb-2">
                    <div class="col-md-6 d-flex align-items-center">
                      <span class="text-muted me-2" style="min-width: 40px;">中文</span>
                      <input type="text" class="form-control" 
                             v-model="ledSettings['service' + i + 'LEDContent']" 
                             :placeholder="`输入服务语${i}的LED显示内容（中文）`"
                             @focus="setFocusedField('service' + i + 'LEDContent', 'zh')">
                    </div>
                    <template v-if="languageSettings && languageSettings.enabled && visibleLanguages.length > 1">
                      <div v-for="(lang, idx) in nonChineseLanguages" :key="'service' + i + '-lang-' + (lang.code || idx)" class="col-md-6 d-flex align-items-center">
                        <span class="text-muted me-2" style="min-width: 40px;">{{ lang.name }}</span>
                        <input type="text" class="form-control" 
                               v-model="ledSettings['service' + i + 'LEDContent_' + lang.code]" 
                               :placeholder="`输入服务语${i}的LED显示内容（${lang.name}）`"
                               @focus="setFocusedField('service' + i + 'LEDContent', lang.code)">
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


    </div>
  </div>
</template>

<script>
export default {
  name: 'LEDControlPanel',
  emits: ['update:ledSettings'],
  props: {
    ledSettings: {
      type: Object,
      required: true
    },
    languageSettings: {
      type: Object,
      default: () => ({
        enabled: false,
        count: 1,
        languages: [{ name: '中文', code: 'zh' }]
      })
    }
  },
  data() {
    return {
      localLedSettings: this.ledSettings,
      // 跟踪当前聚焦的字段和语言
      focusedField: {
        field: '',
        language: 'zh'
      },
      saveTimeout: null,
      isInitialized: false,
      // 跟踪服务语的展开状态
      expandedServices: new Map()
    };
  },
  computed: {
    visibleLanguages() {
      if (!this.languageSettings || !this.languageSettings.languages) return [];
      // 确保语言数量为数字类型，避免 undefined 错误
      const count = parseInt(this.languageSettings.count) || 1;
      return this.languageSettings.languages.slice(0, count);
    },
    // 过滤出非中文的语言列表
    nonChineseLanguages() {
      if (!this.languageSettings || !this.languageSettings.enabled) return [];
      return this.visibleLanguages.filter(lang => lang && lang.code && lang.code !== 'zh');
    }
  },
  methods: {
    validateScrollSettings(type) {
      const settings = type === 'station' ? {
        mode: this.ledSettings.stationNoScrollMode,
        name: '站名'
      } : {
        mode: this.ledSettings.serviceNoScrollMode,
        name: '服务信息'
      };

      if (settings.mode === 'top') {
        alert(`注意：选择${settings.name}向上滚动时，文字将居中显示，且文字数量需要小于屏幕尺寸最大容量。如果文字数量过长，则自动切换为连续向左滚动。`);
      } else if (settings.mode === 'still') {
        alert(`注意：选择${settings.name}静止显示时，文字数量需要小于屏幕尺寸最大容量。如果文字数量过长，则自动切换为连续向左滚动。`);
      }
    },
    // 设置当前聚焦的字段和语言
    setFocusedField(field, language) {
      this.focusedField = {
        field: field,
        language: language
      };
    },
    // 根据当前聚焦的字段添加占位符
    addPlaceholder(placeholder) {
      // 如果没有聚焦的字段，默认使用欢迎语中文字段
      if (!this.focusedField.field) {
        this.focusedField = {
          field: 'welcomeLEDContent',
          language: 'zh'
        };
      }

      // 确定目标字段名
      let targetField;
      if (this.focusedField.language === 'zh') {
        targetField = this.focusedField.field;
      } else {
        targetField = this.focusedField.field + '_' + this.focusedField.language;
      }

      // 确保字段存在
      if (!this.ledSettings[targetField]) {
        this.ledSettings[targetField] = '';
      }

      // 添加占位符
      this.ledSettings[targetField] += placeholder;
    },
    addMultiLanguagePlaceholder(field, placeholder, langCode) {
      if (langCode) {
        const fieldKey = field + '_' + langCode;
        if (!this.ledSettings[fieldKey]) {
          this.ledSettings[fieldKey] = '';
        }
        this.ledSettings[fieldKey] += placeholder;
      } else {
        this.ledSettings[field] += placeholder;
      }
    },
    // 自动保存设置（内部方法）
    autoSaveSettings() {
      // 发送事件通知父组件更新LED设置
      this.$emit('update:ledSettings', this.ledSettings);
    },
    // 切换服务语的展开状态
    toggleServiceExpanded(serviceIndex) {
      this.expandedServices.set(serviceIndex, !this.expandedServices.get(serviceIndex));
    },
    // 检查服务语是否展开
    isServiceExpanded(serviceIndex) {
      return !!this.expandedServices.get(serviceIndex);
    }
  },
  created() {
    // 从localStorage加载保存的设置
    const savedSettings = localStorage.getItem('ledSettings');
    if (savedSettings) {
      // Merge the saved settings into the prop
      Object.assign(this.ledSettings, JSON.parse(savedSettings));
    }
    
    // 延迟标记为已初始化，避免初始加载触发自动保存
    this.$nextTick(() => {
      this.isInitialized = true;
    });
  },
  watch: {
    ledSettings: {
      handler(newSettings) {
        // 保存设置到localStorage
        localStorage.setItem('ledSettings', JSON.stringify(newSettings));
        
        // 只在初始化完成后才自动保存到父组件
        if (this.isInitialized) {
          // 使用防抖延迟保存，避免频繁触发
          clearTimeout(this.saveTimeout);
          this.saveTimeout = setTimeout(() => {
            this.autoSaveSettings();
          }, 500);
        }
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.form-range {
  width: 200px;
}

.gap-1 {
  gap: 0.25rem !important;
}

/* 服务语折叠栏样式 */
.accordion-collapse {
  transition: all 0.35s ease;
}

.accordion-collapse.collapse {
  display: none;
}

.accordion-collapse.collapse.show {
  display: block;
}

.accordion-button {
  transition: all 0.35s ease;
}

.accordion-button.collapsed {
  background-color: #f8f9fa;
}

.accordion-button:not(.collapsed) {
  background-color: #e7f3ff;
  color: #0c63e4;
}
</style> 